import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { customerService } from '../../services/customers/customerService'
import { CreateCustomerForm } from '../../types'
import { showSuccessToast, showErrorToast } from '../../utils/errorHandler'

export const useCustomers = (params?: {
  page?: number
  limit?: number
  search?: string
  is_filer?: boolean
  booker_id?: string | string[]
  area_id?: string | string[]
}) => {
  return useQuery({
    queryKey: ['customers', params],
    queryFn: () => customerService.getCustomers(params),
    refetchOnMount: 'always',
  })
}

export const useCustomer = (id: string) => {
  return useQuery({
    queryKey: ['customer', id],
    queryFn: () => customerService.getCustomerById(id),
    enabled: !!id,
  })
}

export const useCreateCustomer = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (customerData: CreateCustomerForm) => customerService.createCustomer(customerData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] })
      showSuccessToast('Customer created successfully!')
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

export const useUpdateCustomer = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateCustomerForm> }) =>
      customerService.updateCustomer(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['customers'] })
      queryClient.invalidateQueries({ queryKey: ['customer', id] })
      showSuccessToast('Customer updated successfully!')
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

export const useDisableCustomer = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (id: string) => customerService.disableCustomer(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] })
      showSuccessToast('Customer disabled successfully!')
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

export const useExportCustomers = () => {
  return useMutation({
    mutationFn: (params?: { search?: string; is_filer?: boolean; booker_id?: string | string[]; area_id?: string | string[] }) =>
      customerService.exportCustomersHtml(params),
    onSuccess: (htmlContent: string) => {
      const printWindow = window.open('', '_blank')
      if (printWindow) {
        printWindow.document.write(htmlContent)
        printWindow.document.close()
      }
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}
